import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api, type SubcontractorListItem } from '../lib/api'
import { PhoneLink } from '../components/links'

export function SubcontractorsPage() {
  const [subs, setSubs] = useState<SubcontractorListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<SubcontractorListItem | 'new' | null>(null)

  async function refresh() {
    setLoading(true)
    try {
      const res = await api.listSubcontractors()
      setSubs(res.subcontractors)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void refresh()
  }, [])

  async function remove(sub: SubcontractorListItem) {
    if (!window.confirm(`Remove ${sub.name}? This will unassign it from any cases.`)) return
    await api.deleteSubcontractor(sub.id)
    await refresh()
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="page-title">Subcontractor Management</div>
          <p className="page-sub">Manage the subcontractors you assign to cases.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>
          Add New Subcontractor
        </button>
      </div>

      {editing && (
        <SubForm
          sub={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void refresh()
          }}
        />
      )}

      <div className="card" style={{ padding: 0, marginTop: 16 }}>
        {loading ? (
          <div style={{ padding: 20, color: 'var(--gray)' }}>Loading…</div>
        ) : subs.length === 0 ? (
          <div style={{ padding: 20, color: 'var(--gray)' }}>No subcontractors yet.</div>
        ) : (
          <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Main Contact</th>
                <th>Phone</th>
                <th>Specialty</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>
                    <Link to={`/app/subcontractors/${s.id}`}>{s.name}</Link>
                  </td>
                  <td>
                    {s.main_contact_name ?? '—'}
                    {s.main_contact_title && (
                      <span style={{ color: 'var(--gray)' }}> · {s.main_contact_title}</span>
                    )}
                  </td>
                  <td><PhoneLink phone={s.main_contact_phone} /></td>
                  <td>{s.specialty ?? '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="link-btn" onClick={() => setEditing(s)}>
                      Edit
                    </button>
                    <button
                      className="link-btn"
                      style={{ color: 'var(--red)', marginLeft: 14 }}
                      onClick={() => void remove(s)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  )
}

function SubForm({
  sub,
  onClose,
  onSaved,
}: {
  sub: SubcontractorListItem | null
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(sub?.name ?? '')
  const [specialty, setSpecialty] = useState(sub?.specialty ?? '')
  // Only collected when creating — seeds the first (main) contact.
  const [contact, setContact] = useState('')
  const [title, setTitle] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    setError(null)
    setBusy(true)
    try {
      if (sub) {
        await api.updateSubcontractor(sub.id, { name: name.trim(), specialty })
      } else {
        const { id } = await api.createSubcontractor({ name: name.trim(), specialty })
        if (contact.trim() || phone.trim() || email.trim()) {
          await api.addSubContact(id, {
            name: contact.trim() || 'Contact',
            title,
            phone,
            email,
            is_main: true,
          })
        }
      }
      onSaved()
    } catch {
      setError('Could not save. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card" style={{ marginTop: 16, maxWidth: 520 }} onSubmit={onSubmit}>
      <div style={{ fontWeight: 700, marginBottom: 12 }}>
        {sub ? 'Edit subcontractor' : 'New subcontractor'}
      </div>
      <div className="field">
        <label>Name *</label>
        <input value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="field">
        <label>Specialty</label>
        <input
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          placeholder="e.g. Roofing, Plumbing"
        />
      </div>

      {sub ? (
        <p style={{ fontSize: 13, color: 'var(--gray)', margin: '4px 0 12px' }}>
          Manage contacts on the{' '}
          <Link to={`/app/subcontractors/${sub.id}`}>subcontractor's profile</Link>.
        </p>
      ) : (
        <>
          <div style={{ fontWeight: 700, margin: '16px 0 8px' }}>Main contact</div>
          <div className="field">
            <label>Contact person</label>
            <input value={contact} onChange={(e) => setContact(e.target.value)} />
          </div>
          <div className="field">
            <label>Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Owner, Project Manager"
            />
          </div>
          <div className="field">
            <label>Phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="field">
            <label>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </>
      )}

      {error && <p className="form-error">{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="btn"
          style={{ border: '1px solid var(--line)' }}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
